import type { ReactNode } from 'react';
import { PriceAndRights } from './PriceAndRights.js';
import { CompatibilityBadge } from './CompatibilityBadge.js';

export interface AssetCardData {
  id: string;
  title: string;
  posterUrl?: string | null;
  creatorName?: string;
  priceLabel?: string | null;
  compatibility?: 'passed' | 'failed' | 'stale' | 'untested';
}

export interface AssetCardProps {
  item: AssetCardData;
  href: string;
  aspectRatio?: '1:1' | '4:5' | '16:9';
  footer?: ReactNode;
}

const aspectStyle: Record<string, string> = {
  '1:1': '1 / 1',
  '4:5': '4 / 5',
  '16:9': '16 / 9',
};

export function AssetCard({ item, href, aspectRatio = '1:1', footer }: AssetCardProps) {
  return (
    <article
      style={{
        breakInside: 'avoid',
        background: 'var(--3ds-canvas, #fff)',
        border: '1px solid var(--3ds-border-subtle, #DEDEDE)',
        borderRadius: 'var(--3ds-radius-card, 20px)',
        overflow: 'hidden',
      }}
    >
      <a href={href} aria-label={`View ${item.title}`}>
        {item.posterUrl ? (
          <img
            src={item.posterUrl}
            alt=""
            loading="lazy"
            style={{
              width: '100%',
              aspectRatio: aspectStyle[aspectRatio],
              objectFit: 'cover',
              background: 'var(--3ds-search-surface, #E9E9E9)',
              display: 'block',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: aspectStyle[aspectRatio],
              background: 'var(--3ds-search-surface, #E9E9E9)',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--3ds-text-secondary)',
              fontSize: '0.875rem',
            }}
          >
            No poster
          </div>
        )}
      </a>
      <div style={{ padding: '0.85rem 1rem 1rem', display: 'grid', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }}>{item.title}</h3>
        {item.creatorName ? (
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--3ds-text-secondary)' }}>{item.creatorName}</p>
        ) : null}
        <PriceAndRights priceLabel={item.priceLabel} compact />
        <CompatibilityBadge status={item.compatibility ?? 'untested'} />
        {footer}
      </div>
    </article>
  );
}
